import { useEffect, useRef, useCallback } from 'react';

interface UseDebouncedSaveOptions<T> {
  data: T | null;
  onSave: (data: T) => void;
  delay?: number;
  enabled?: boolean;
}

export function useDebouncedSave<T>({
  data,
  onSave,
  delay = 2000,
  enabled = true
}: UseDebouncedSaveOptions<T>) {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const previousDataRef = useRef<T | null>(null);

  const debouncedSave = useCallback(() => {
    if (data && enabled && JSON.stringify(data) !== JSON.stringify(previousDataRef.current)) {
      onSave(data);
      previousDataRef.current = data;
    }
  }, [data, onSave, enabled]);

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (data && enabled) {
      timeoutRef.current = setTimeout(debouncedSave, delay);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data, delay, enabled, debouncedSave]);

  // Save immediately when component unmounts
  useEffect(() => {
    return () => {
      if (data && enabled && JSON.stringify(data) !== JSON.stringify(previousDataRef.current)) {
        onSave(data);
      }
    };
  }, [data, onSave, enabled]);

  const saveNow = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    debouncedSave();
  }, [debouncedSave]);

  return { saveNow };
}